import { useEffect, useState } from "react";
import type { useIssues } from "../../controllers/useIssues";
import { useUserDirectory } from "../../controllers/useUserDirectory";
import { MENTION_ID_RE, UserDirError, UserInline, humanizeMentions, userOptionLabel } from "../components/UserCell";
import type { ProjectMember } from "../../models/account";
import { issuePriorities, issueStatuses, issueTypes } from "../../controllers/projectApi";
import type { IssueActivityType, IssuePriority, IssueStatus, IssueType } from "../../models/project";
import type { WorkAreaType } from "../../models/eqms";
import { WORK_AREAS, WORK_AREA_SHORT_LABEL } from "../../models/eqms";

const TYPE_LABEL: Record<IssueType, string> = {
  EPIC: "에픽", STORY: "스토리", TASK: "작업", BUG: "버그", SUBTASK: "하위 작업",
  REQUIREMENT: "요구사항", DESIGN: "디자인", TEST_CLASS: "테스트 분류", TEST_ITEM: "테스트 항목",
};
const PRIORITY_LABEL: Record<IssuePriority, string> = {
  HIGHEST: "최상", HIGH: "높음", MEDIUM: "보통", LOW: "낮음", LOWEST: "최하",
};
const STATUS_LABEL: Record<IssueStatus, string> = {
  BACKLOG: "백로그", TODO: "할 일", IN_PROGRESS: "진행 중", IN_REVIEW: "검토 중", DONE: "완료",
};
const ACTIVITY_LABEL: Record<IssueActivityType, string> = {
  CREATED: "생성됨", STATUS_CHANGED: "상태 변경", ASSIGNEE_CHANGED: "담당자 변경",
  PRIORITY_CHANGED: "우선순위 변경", TYPE_CHANGED: "유형 변경", POINTS_CHANGED: "포인트 변경",
  SPRINT_CHANGED: "스프린트 변경", RESOLVED: "해결됨", REOPENED: "재오픈", COMMENTED: "댓글 작성",
};

// One issue's full detail: inline-editable fields, comments (with @mention -> mentionedUserIds),
// subtasks, and the activity timeline.
export function IssueDetailView({
  issues,
  members,
}: {
  issues: ReturnType<typeof useIssues>;
  members: ProjectMember[];
}) {
  const issue = issues.selectedIssue!;
  const [commentBody, setCommentBody] = useState("");
  const [labelsText, setLabelsText] = useState(issue.labels.join(", "));
  // Mentions the composer inserted as readable `@이름` text, kept alongside so the body stays human
  // while the wire payload still carries real user ids.
  const [mentionPicks, setMentionPicks] = useState<{ id: string; token: string }[]>([]);

  const dir = useUserDirectory(issues.getToken);
  useEffect(() => {
    dir.ensure([
      issue.reporterId,
      issue.assigneeId,
      ...members.map((m) => m.userId),
      ...issues.comments.map((c) => c.authorId),
    ]);
  }, [issue.reporterId, issue.assigneeId, members, issues.comments, dir.ensure]);

  /**
   * The `@…` text inserted for a member. Two people can share a 이름, and a bare name would then
   * tag the wrong person — so a colliding label is disambiguated with the email.
   */
  function mentionToken(userId: string): string {
    const label = dir.label(userId);
    const collides = members.some((m) => m.userId !== userId && dir.label(m.userId) === label);
    return `@${collides ? userOptionLabel(userId, dir) : label}`;
  }

  function mentionsIn(text: string): string[] {
    const ids = new Set<string>();
    // Names picked in this composer, still present in the text.
    for (const pick of mentionPicks) {
      if (text.includes(pick.token)) ids.add(pick.id);
    }
    // Legacy/pasted raw-id tokens.
    for (const m of text.matchAll(MENTION_ID_RE)) ids.add(m[1]);
    return [...ids];
  }

  /** Drop picks whose text the author has since edited away, so a stale pick can't re-tag someone. */
  function editComment(text: string) {
    setCommentBody(text);
    setMentionPicks((cur) => cur.filter((p) => text.includes(p.token)));
  }

  function addMention(userId: string) {
    const token = mentionToken(userId);
    setMentionPicks((cur) => (cur.some((p) => p.id === userId) ? cur : [...cur, { id: userId, token }]));
    setCommentBody((b) => `${b}${token} `);
  }

  async function submitComment() {
    if (!commentBody.trim()) return;
    await issues.addComment({ body: commentBody, mentionedUserIds: mentionsIn(commentBody) });
    setCommentBody("");
    setMentionPicks([]);
  }

  async function saveLabels() {
    const labels = labelsText.split(",").map((s) => s.trim()).filter(Boolean);
    await issues.setLabels(issue.id, labels);
  }

  async function toggleWorkArea(area: WorkAreaType) {
    const next = issue.workAreaTypes.includes(area)
      ? issue.workAreaTypes.filter((a) => a !== area)
      : [...issue.workAreaTypes, area];
    await issues.setWorkAreas(issue.id, next);
  }

  return (
    <div>
      <div style={{ display: "flex", gap: 10, marginBottom: 8 }}>
        <button className="btn ghost" style={{ width: "auto" }} onClick={issues.backToBoard}>← 이슈 목록</button>
        <button className="btn ghost" style={{ width: "auto" }} onClick={() => {
          if (window.confirm("이 이슈를 삭제하시겠습니까?")) void issues.removeIssue(issue.id);
        }}>삭제</button>
      </div>

      {issues.error && <div className="note" style={{ background: "#fdeef0", marginBottom: 16 }}><b style={{ color: "var(--amber)" }}>오류:</b> {issues.error}</div>}

      <div className="detail-name">
        <span className="mono">{issue.key}</span>
        <h1 style={{ fontSize: 24 }}>{issue.summary}</h1>
      </div>

      {issues.loadingDetail ? (
        <div className="center">불러오는 중…</div>
      ) : (
        <div className="detail-body">
          <div className="detail-main">
            <h3>설명</h3>
            <p style={{ whiteSpace: "pre-wrap" }}>{issue.description || "설명 없음"}</p>

            <UserDirError dir={dir} />
            <h3>댓글 · {issues.comments.length}</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 12 }}>
              {issues.comments.map((c) => (
                <div key={c.id} className="screen-row" style={{ flexDirection: "column", alignItems: "stretch", gap: 4 }}>
                  <span style={{ fontSize: 12, color: "var(--ink3)" }}>
                    <b><UserInline id={c.authorId} dir={dir} /></b> · {new Date(c.createdAt).toLocaleString()}
                  </span>
                  <span style={{ fontSize: 14 }}>{humanizeMentions(c.body, dir)}</span>
                </div>
              ))}
              {issues.comments.length === 0 && <div className="sdui-list-empty">댓글이 없습니다.</div>}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <input
                className="sdui-input"
                placeholder="댓글 작성 (아래 멤버를 눌러 멘션)"
                value={commentBody}
                onChange={(e) => editComment(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") void submitComment(); }}
              />
              <button className="btn primary" style={{ width: "auto" }} disabled={!commentBody.trim()} onClick={submitComment}>등록</button>
            </div>
            {members.length > 0 && (
              <p style={{ fontSize: 12, color: "var(--ink3)", marginTop: 6 }}>
                멘션: {members.map((m) => (
                  <button key={m.userId} className="tag" style={{ border: "none", marginRight: 4 }}
                    // Until the name resolves, inserting would freeze a short-id into the comment body.
                    disabled={!dir.get(m.userId)}
                    title={dir.get(m.userId) ? dir.detail(m.userId) : "이름을 불러오는 중…"}
                    onClick={() => addMention(m.userId)}>
                    @{dir.label(m.userId)}
                  </button>
                ))}
              </p>
            )}

            <h3 style={{ marginTop: 24 }}>활동 내역</h3>
            <div className="timeline">
              {issues.activity.map((a) => (
                <div key={a.id} className="timeline-item">
                  <span className="timeline-dot" />
                  <span>
                    <b>{ACTIVITY_LABEL[a.type]}</b>
                    {a.field && ` · ${a.field}`}
                    {(a.oldValue || a.newValue) && ` (${a.oldValue ?? "—"} → ${a.newValue ?? "—"})`}
                    {" · "}{new Date(a.createdAt).toLocaleString()}
                  </span>
                </div>
              ))}
              {issues.activity.length === 0 && <div className="sdui-list-empty">활동 내역이 없습니다.</div>}
            </div>

            {issues.subtasks.length > 0 && (
              <>
                <h3 style={{ marginTop: 24 }}>하위 작업</h3>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {issues.subtasks.map((s) => (
                    <button key={s.id} className="screen-row" style={{ border: "none", textAlign: "left" }} onClick={() => issues.openIssue(s.id)}>
                      <span className="mono">{s.key}</span> {s.summary}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          <aside className="aside">
            <div className="info-card">
              <h4>필드</h4>
              <label className="sdui-field">
                <span className="sdui-label">상태</span>
                <select className="sdui-input" value={issue.status} onChange={(e) => void issues.setStatus(issue.id, e.target.value as IssueStatus)}>
                  {issueStatuses.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                </select>
              </label>
              <label className="sdui-field">
                <span className="sdui-label">유형</span>
                <select className="sdui-input" value={issue.type} onChange={(e) => void issues.setType(issue.id, e.target.value as IssueType)}>
                  {issueTypes.map((t) => <option key={t} value={t}>{TYPE_LABEL[t]}</option>)}
                </select>
              </label>
              <label className="sdui-field">
                <span className="sdui-label">우선순위</span>
                <select className="sdui-input" value={issue.priority} onChange={(e) => void issues.setPriority(issue.id, e.target.value as IssuePriority)}>
                  {issuePriorities.map((p) => <option key={p} value={p}>{PRIORITY_LABEL[p]}</option>)}
                </select>
              </label>
              <label className="sdui-field">
                <span className="sdui-label">담당자</span>
                <select
                  className="sdui-input"
                  value={issue.assigneeId ?? ""}
                  onChange={(e) => void issues.setAssignee(issue.id, e.target.value || null)}
                >
                  <option value="">미배정</option>
                  {members.map((m) => <option key={m.userId} value={m.userId}>{userOptionLabel(m.userId, dir)}</option>)}
                </select>
              </label>
              <label className="sdui-field">
                <span className="sdui-label">스토리 포인트</span>
                <input
                  className="sdui-input" type="number" min={0} step={0.5}
                  value={issue.storyPoints ?? ""}
                  onChange={(e) => void issues.setPoints(issue.id, e.target.value === "" ? null : Number(e.target.value))}
                />
              </label>
              <label className="sdui-field">
                <span className="sdui-label">스프린트</span>
                <select
                  className="sdui-input"
                  value={issue.sprintId ?? ""}
                  onChange={(e) => void issues.moveToSprint(issue.id, e.target.value || null)}
                >
                  <option value="">백로그</option>
                  {issues.sprints.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </label>
              <label className="sdui-field">
                <span className="sdui-label">업무유형 (복수 선택)</span>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {WORK_AREAS.map((a) => {
                    const on = issue.workAreaTypes.includes(a);
                    return (
                      <button
                        key={a}
                        type="button"
                        className="tag"
                        style={{ cursor: "pointer", border: on ? "1.5px solid var(--blue)" : "1px solid var(--line, #d0d5dd)", color: on ? "var(--blue)" : undefined }}
                        onClick={() => void toggleWorkArea(a)}
                      >
                        {on ? "✓ " : ""}{WORK_AREA_SHORT_LABEL[a]}
                      </button>
                    );
                  })}
                </div>
              </label>
              <label className="sdui-field">
                <span className="sdui-label">레이블 (쉼표 구분)</span>
                <input className="sdui-input" value={labelsText} onChange={(e) => setLabelsText(e.target.value)} onBlur={saveLabels} />
              </label>
              <div className="kv"><span>리포터</span><span><UserInline id={issue.reporterId} dir={dir} /></span></div>
              <div className="kv"><span>워처</span><span>{issue.watcherIds.length}명</span></div>
              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                <button className="btn ghost" style={{ width: "auto" }} onClick={() => void issues.toggleWatch(issue.id, true)}>지켜보기</button>
                <button className="btn ghost" style={{ width: "auto" }} onClick={() => void issues.toggleWatch(issue.id, false)}>지켜보기 해제</button>
              </div>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
